import React, { useState, useEffect, useCallback, useMemo } from 'react'
import DashboardLayout from '../../components/DashboardLayout'
import EnrollmentManagementTabs from '../../components/admin/EnrollmentManagementTabs'
import { enrollmentService, userService } from '../../api'
import { useAuth } from '../../context/AuthContext'
import toast from 'react-hot-toast'
import {
  FileText, Download, Upload, Calendar, User, Book, X, Check, Filter,
} from 'lucide-react'
import ListPageHeader from '../../components/list/ListPageHeader'
import ListToolbar from '../../components/list/ListToolbar'
import ListPagination from '../../components/list/ListPagination'
import ListEmptyState from '../../components/list/ListEmptyState'
import ListLoadingState from '../../components/list/ListLoadingState'
import DataRow, { DataRowList } from '../../components/list/DataRow'
import StatusPill from '../../components/list/StatusPill'
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers'
import AppSelect from '../../components/ui/AppSelect'
import { formatZar } from '../../utils/currency'

const PAYMENT_STATUS_FILTERS = [
  { value: 'all', label: 'All paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
]

const PAYMENT_STATUS_LABELS = {
  pending: 'Payment pending',
  verified: 'Payment verified',
  rejected: 'Payment rejected',
  not_required: 'Not required',
}

const getReceiptDetails = (enrollment) =>
  enrollment?.payment_receipt_details
  || enrollment?.payment_receipt
  || null

const getReceiptDownloadUrl = (receipt) =>
  receipt?.download_url
  || receipt?.file_url
  || receipt?.file
  || null

function PaymentVerification() {
  const { user } = useAuth()
  const canManage = ['finance', 'admin', 'campus_admin', 'superuser'].includes(user?.user_type)
    || user?.is_staff
    || user?.is_superuser

  const [enrollments, setEnrollments] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE)
  const [actionLoading, setActionLoading] = useState(null)

  const [uploadTarget, setUploadTarget] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejecting, setRejecting] = useState(false)

  const fetchSummary = useCallback(async () => {
    try {
      const res = await enrollmentService.getPaymentVerificationSummary()
      setSummary(res.data || null)
    } catch {
      setSummary(null)
    }
  }, [])

  const fetchEnrollments = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        payment_required: true,
        page,
        page_size: searchTerm ? 100 : pageSize,
      }
      if (statusFilter !== 'all') params.payment_status = statusFilter

      const res = await enrollmentService.getAllEnrollments(params)
      const { results, count } = parsePaginated(res)
      setEnrollments(results)
      setTotalCount(searchTerm ? results.length : count)
    } catch {
      toast.error('Failed to load payment verifications')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, searchTerm, statusFilter])

  useEffect(() => {
    if (!canManage) return
    fetchEnrollments()
    fetchSummary()
  }, [canManage, fetchEnrollments, fetchSummary])

  useEffect(() => {
    setPage(1)
  }, [searchTerm, statusFilter])

  const filteredEnrollments = useMemo(() => {
    const q = searchTerm.toLowerCase().trim()
    if (!q) return enrollments
    return enrollments.filter((e) =>
      e.student_name?.toLowerCase().includes(q)
      || e.student_email?.toLowerCase().includes(q)
      || e.course_title?.toLowerCase().includes(q)
    )
  }, [enrollments, searchTerm])

  const displayCount = searchTerm ? filteredEnrollments.length : totalCount
  const pagedDisplay = searchTerm
    ? filteredEnrollments.slice((page - 1) * pageSize, page * pageSize)
    : filteredEnrollments

  const handleUpload = async (event) => {
    event.preventDefault()
    if (!uploadTarget || !uploadFile) {
      toast.error('Choose a receipt file to upload')
      return
    }

    setUploading(true)
    try {
      const data = new FormData()
      data.append('file', uploadFile)
      data.append('document_type', 'payment_receipt')
      data.append('enrollment', uploadTarget.id)
      await userService.uploadDocument(data)
      toast.success(getReceiptDetails(uploadTarget) ? 'Receipt replaced' : 'Receipt uploaded')
      setUploadTarget(null)
      setUploadFile(null)
      fetchEnrollments()
      fetchSummary()
    } catch {
      toast.error('Receipt upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleVerify = async (enrollment) => {
    const receipt = getReceiptDetails(enrollment)
    if (!receipt?.id) {
      toast.error('No receipt to verify')
      return
    }
    setActionLoading(`verify-${enrollment.id}`)
    try {
      await userService.verifyDocument(receipt.id)
      toast.success('Payment receipt verified. Enrollment still needs admin approval when pending.')
      fetchEnrollments()
      fetchSummary()
    } catch {
      toast.error('Failed to verify receipt')
    } finally {
      setActionLoading(null)
    }
  }

  const handleRejectSubmit = async (event) => {
    event.preventDefault()
    const receipt = getReceiptDetails(rejectTarget)
    if (!receipt?.id) {
      toast.error('No receipt to reject')
      return
    }
    setRejecting(true)
    try {
      await userService.rejectDocument(receipt.id, rejectReason.trim() || undefined)
      toast.success('Payment receipt rejected')
      setRejectTarget(null)
      setRejectReason('')
      fetchEnrollments()
      fetchSummary()
    } catch {
      toast.error('Failed to reject receipt')
    } finally {
      setRejecting(false)
    }
  }

  const statusFilterControl = (
    <AppSelect
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      aria-label="Filter by payment status"
    >
      {PAYMENT_STATUS_FILTERS.map((opt) => {
        const count = summary?.[opt.value]
        const suffix = opt.value !== 'all' && typeof count === 'number' ? ` (${count})` : ''
        return (
          <option key={opt.value} value={opt.value}>
            {opt.label}{suffix}
          </option>
        )
      })}
    </AppSelect>
  )

  if (!canManage) {
    return (
      <DashboardLayout>
        <main className="page-container content-width">
          <p style={{ color: 'var(--color-text-secondary)' }}>You do not have access to payment verification.</p>
        </main>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <main className="page-container content-width ux-list-page">
        <ListPageHeader
          kicker="Enrollment Management"
          kickerIcon={FileText}
          title="Payment Verification"
          description="Review paid enrollments and verify receipt documents. Verifying a receipt does not approve the enrollment."
        />

        <EnrollmentManagementTabs active="payment-verification" />

        <ListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by student or course…"
          filters={(
            <>
              <Filter size={16} style={{ color: 'var(--color-text-muted)' }} aria-hidden="true" />
              {statusFilterControl}
            </>
          )}
        />

        {loading ? (
          <ListLoadingState label="Loading payment records…" />
        ) : pagedDisplay.length === 0 ? (
          <ListEmptyState
            icon={FileText}
            title="No paid enrollments found"
            description="No payment records match your search or filters."
            action={(
              <button
                type="button"
                className="ux-btn-sm ux-btn-ghost"
                onClick={() => { setSearchTerm(''); setStatusFilter('all') }}
              >
                Clear filters
              </button>
            )}
          />
        ) : (
          <DataRowList>
            {pagedDisplay.map((enrollment, index) => {
              const receipt = getReceiptDetails(enrollment)
              const downloadUrl = getReceiptDownloadUrl(receipt)
              const paymentStatus = enrollment.payment_status || 'pending'
              const canVerify = Boolean(receipt?.id) && paymentStatus !== 'verified'
              const canRejectReceipt = Boolean(receipt?.id) && paymentStatus !== 'rejected'

              return (
                <DataRow
                  key={enrollment.id}
                  index={index}
                  leading={(
                    <div className="ux-row-thumb">
                      <FileText size={20} />
                    </div>
                  )}
                  title={enrollment.student_name || 'Student'}
                  subtitle={enrollment.student_email || '—'}
                  meta={(
                    <>
                      <span><Book size={12} /> {enrollment.course_title}</span>
                      {enrollment.course_price != null && (
                        <span>{formatZar(enrollment.course_price)}</span>
                      )}
                      <span>
                        <Calendar size={12} />
                        {' '}
                        {new Date(enrollment.enrolled_at).toLocaleDateString('en-ZA')}
                      </span>
                    </>
                  )}
                  badges={(
                    <StatusPill
                      status={paymentStatus}
                      label={PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus}
                    />
                  )}
                  actions={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                      {downloadUrl ? (
                        <a
                          href={downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ux-btn-sm ux-btn-ghost"
                          style={{ textDecoration: 'none' }}
                          aria-label={`Download receipt for ${enrollment.student_name || 'student'}`}
                        >
                          <Download size={14} /> Receipt
                        </a>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>No file</span>
                      )}

                      <button
                        type="button"
                        className="ux-icon-btn"
                        title={receipt ? 'Replace receipt' : 'Upload receipt'}
                        aria-label={receipt ? 'Replace payment receipt' : 'Upload payment receipt'}
                        onClick={() => { setUploadTarget(enrollment); setUploadFile(null) }}
                      >
                        <Upload size={16} />
                      </button>

                      {canVerify && (
                        <button
                          type="button"
                          className="ux-icon-btn success"
                          title="Verify payment receipt"
                          aria-label="Verify payment receipt"
                          disabled={actionLoading === `verify-${enrollment.id}`}
                          onClick={() => handleVerify(enrollment)}
                        >
                          <Check size={16} />
                        </button>
                      )}

                      {canRejectReceipt && (
                        <button
                          type="button"
                          className="ux-icon-btn danger"
                          title="Reject payment receipt"
                          aria-label="Reject payment receipt"
                          onClick={() => { setRejectTarget(enrollment); setRejectReason('') }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  )}
                />
              )
            })}
          </DataRowList>
        )}

        {!loading && pagedDisplay.length > 0 && (
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={displayCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
          />
        )}
      </main>

      {uploadTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-md bg-black bg-opacity-50 backdrop-blur-sm">
          <div
            className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-receipt-title"
          >
            <div className="p-xl border-b border-gray-50 flex justify-between items-center">
              <h3 id="upload-receipt-title" className="text-h5 font-bold">
                {getReceiptDetails(uploadTarget) ? 'Replace Receipt' : 'Upload Receipt'}
              </h3>
              <button
                type="button"
                onClick={() => { setUploadTarget(null); setUploadFile(null) }}
                className="p-sm hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close upload dialog"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUpload} className="p-xl space-y-md">
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                <User size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
                {' '}
                {uploadTarget.student_name}
                {' · '}
                {uploadTarget.course_title}
              </p>
              <div className="space-y-sm">
                <label className="block text-xs font-bold text-theme-muted uppercase" htmlFor="payment-receipt-file">
                  Document file
                </label>
                <div className="relative">
                  <input
                    type="file"
                    className="hidden"
                    id="payment-receipt-file"
                    accept=".pdf,image/*"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                  <label
                    htmlFor="payment-receipt-file"
                    className="flex items-center justify-center gap-sm w-full py-md border-2 border-dashed border-theme-border rounded-2xl cursor-pointer hover:border-primary hover:text-primary transition-all bg-theme-bg"
                  >
                    <Upload size={18} />
                    {uploadFile ? uploadFile.name : 'Select PDF or Image'}
                  </label>
                </div>
              </div>
              <button
                type="submit"
                disabled={uploading || !uploadFile}
                className="w-full bg-primary text-white py-md rounded-pill font-bold shadow-sm hover:opacity-90 transition-all mt-lg disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : 'Upload Receipt'}
              </button>
            </form>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-md bg-black bg-opacity-50 backdrop-blur-sm">
          <div
            className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-receipt-title"
          >
            <div className="p-xl border-b border-gray-50 flex justify-between items-center">
              <h3 id="reject-receipt-title" className="text-h5 font-bold">Reject Payment Receipt</h3>
              <button
                type="button"
                onClick={() => { setRejectTarget(null); setRejectReason('') }}
                className="p-sm hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close rejection dialog"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleRejectSubmit} className="p-xl space-y-md">
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Rejecting a receipt does not change enrollment approval status. Provide a reason for the student record.
              </p>
              <div className="space-y-sm">
                <label className="block text-xs font-bold text-theme-muted uppercase" htmlFor="reject-receipt-reason">
                  Reason (optional)
                </label>
                <textarea
                  id="reject-receipt-reason"
                  rows={4}
                  className="w-full px-md py-sm rounded-xl border border-theme-border outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this receipt was rejected"
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="ux-btn-sm ux-btn-ghost"
                  onClick={() => { setRejectTarget(null); setRejectReason('') }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={rejecting}
                  className="ux-btn-sm ux-btn-primary"
                  style={{ background: 'var(--color-danger, #dc2626)' }}
                >
                  {rejecting ? 'Rejecting…' : 'Reject receipt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}

export default PaymentVerification
